import type { google } from '@alugha/ima';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { ErrorContainer } from '../CreativeMediaError/ErrorContainer.tsx';
import { StyledMediaContainerDiv } from '../CreativeMediaError/Preview.styled.tsx';
import { readSavedVolume } from './imaSdkUtils.ts';
import { useImaAd } from './useImaAd.ts';
import { FullscreenButton } from './videoControls/FullscreenButton.tsx';
import { PlayPauseButton } from './videoControls/PlayPauseButton.tsx';
import { ProgressBar } from './videoControls/ProgressBar.tsx';
import { TimeDisplay } from './videoControls/TimeDisplay.tsx';
import { VolumeControl } from './videoControls/VolumeControl.tsx';

type VideoPlayerWithControlsProps = {
  adMarkup: string;
  onClose: () => void;
  width: number;
  height: number;
};

export function VideoPlayerWithControls({ adMarkup, onClose, width, height }: VideoPlayerWithControlsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const savedVolume = readSavedVolume();
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(savedVolume);
  const prevVolumeRef = useRef(savedVolume || 1);

  const { adContainerRef, adManagerRef, isError } = useImaAd(adMarkup, {
    initDimensions: [width, height],
    onAdsManagerReady: (manager, ima) => {
      manager.setVolume(savedVolume);

      manager.addEventListener(ima.AdEvent.Type.AD_PROGRESS, (adProgressEvent: google.ima.AdEvent) => {
        const data = adProgressEvent.getAdData() as { currentTime: number; duration: number };

        setCurrentTime(data.currentTime);
        setDuration(data.duration);
      });

      manager.addEventListener(ima.AdEvent.Type.PAUSED, () => setIsPaused(true));
      manager.addEventListener(ima.AdEvent.Type.RESUMED, () => setIsPaused(false));

      manager.addEventListener(ima.AdEvent.Type.VOLUME_CHANGED, () => {
        const vol = manager.getVolume();

        if (vol > 0) prevVolumeRef.current = vol;

        setVolume(vol);
      });

      manager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, () => onClose());
      manager.addEventListener(ima.AdEvent.Type.CLICK, () => manager.resume());
    },
  });

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreen = !!document.fullscreenElement;

      setIsFullscreen(fullscreen);
      adManagerRef.current?.resize(
        fullscreen ? window.screen.width : width,
        fullscreen ? window.screen.height : height,
        (fullscreen ? 'fullscreen' : 'normal') as google.ima.ViewMode
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [adManagerRef, width, height]);

  if (isError) {
    return <ErrorContainer height={height} width={width} />;
  }

  return (
    <Box
      ref={containerRef}
      data-test="video-player-with-controls"
      sx={{
        display: isFullscreen ? 'block' : 'inline-flex',
        position: 'relative',
        ...(isFullscreen && { width: '100%', height: '100%' }),
      }}
    >
      <StyledMediaContainerDiv
        ref={adContainerRef}
        width={isFullscreen ? window.screen.width : width}
        height={isFullscreen ? window.screen.height : height}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          backgroundColor: theme => alpha(theme.palette.common.black, 0.75),
        }}
      >
        <PlayPauseButton isPaused={isPaused} manager={adManagerRef.current} />
        <ProgressBar
          currentTime={currentTime}
          duration={duration}
          manager={adManagerRef.current}
          adContainerRef={adContainerRef}
          setCurrentTime={setCurrentTime}
        />
        <TimeDisplay currentTime={currentTime} duration={duration} />
        <VolumeControl
          volume={volume}
          manager={adManagerRef.current}
          setVolume={setVolume}
          prevVolumeRef={prevVolumeRef}
          compact={width <= 400}
        />
        <FullscreenButton isFullscreen={isFullscreen} containerRef={containerRef} />
      </Box>
    </Box>
  );
}
